import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, Users, BookOpen, ClipboardList, FileQuestion,
  AlertTriangle, CheckCircle, Clock, Eye, ChevronDown, ChevronUp,
  Search, Filter, Download, BarChart3, Target, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [sortBy, setSortBy] = useState('needs_attention');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedStudent, setExpandedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState({});

  useEffect(() => {
    fetchData();
  }, [selectedCourse, sortBy]);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const headers = getHeaders();
      const courseParam = selectedCourse !== 'all' ? `?course_id=${selectedCourse}` : '';
      const studentParams = `?sort_by=${sortBy}${selectedCourse !== 'all' ? `&course_id=${selectedCourse}` : ''}`;

      const [overviewRes, studentsRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/analytics/overview${courseParam}`, { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API_URL}/api/lms/analytics/students${studentParams}`, { headers }).then(r => r.json()).catch(() => ({ students: [] })),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setOverview(overviewRes);
      setStudents(studentsRes.students || []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (studentId) => {
    if (studentDetails[studentId]) {
      setExpandedStudent(expandedStudent === studentId ? null : studentId);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/lms/analytics/student/${studentId}`, {
        headers: getHeaders()
      });
      const data = await response.json();
      setStudentDetails(prev => ({ ...prev, [studentId]: data }));
      setExpandedStudent(studentId);
    } catch (error) {
      toast.error('Failed to load student details');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-100 text-green-700 border-green-200';
      case 'on_track': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'behind': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'needs_attention': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'excellent': return 'Excellent';
      case 'on_track': return 'On Track';
      case 'behind': return 'Behind';
      case 'needs_attention': return 'Needs Attention';
      default: return status;
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const filteredStudents = students.filter(s => 
    s.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.student_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const atRiskCount = students.filter(s => s.status === 'needs_attention').length;
  const excellentCount = students.filter(s => s.status === 'excellent').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/lms" className="p-2 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="h-6 w-6" />
                Student Progress Analytics
              </h1>
              <p className="text-indigo-200 text-sm">Track student learning progress and identify who needs help</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4 text-center">
                <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold text-blue-700">{overview.active_students || 0}</p>
                <p className="text-xs text-blue-600">Active Students</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4 text-center">
                <BookOpen className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">{overview.content_completions || 0}</p>
                <p className="text-xs text-green-600">Content Views</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4 text-center">
                <ClipboardList className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                <p className="text-2xl font-bold text-purple-700">{overview.total_submissions || 0}</p>
                <p className="text-xs text-purple-600">Submissions</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4 text-center">
                <FileQuestion className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                <p className="text-2xl font-bold text-orange-700">{overview.average_quiz_score || 0}%</p>
                <p className="text-xs text-orange-600">Avg Quiz Score</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-600" />
                <p className="text-2xl font-bold text-red-700">{atRiskCount}</p>
                <p className="text-xs text-red-600">Need Attention</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4 text-center">
                <Award className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
                <p className="text-2xl font-bold text-emerald-700">{excellentCount}</p>
                <p className="text-xs text-emerald-600">Top Performers</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="needs_attention">Needs Attention First</SelectItem>
                  <SelectItem value="progress_asc">Progress (Low to High)</SelectItem>
                  <SelectItem value="progress_desc">Progress (High to Low)</SelectItem>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Student Progress List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Target className="h-5 w-5 text-indigo-600" />
                Student Progress ({filteredStudents.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredStudents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No student data available yet</p>
                <p className="text-sm">Students will appear here once they start using the LMS</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredStudents.map((student) => (
                  <div key={student.student_db_id} className="border rounded-lg overflow-hidden">
                    {/* Student Row */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => fetchStudentDetails(student.student_db_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            {student.photo_url ? (
                              <img src={student.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <span className="text-indigo-600 font-semibold">
                                {student.student_name?.charAt(0) || '?'}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{student.student_name}</p>
                            <p className="text-xs text-gray-500">{student.student_id} • {student.course_name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Progress Bar */}
                          <div className="hidden md:block w-32">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-500">Progress</span>
                              <span className="font-semibold">{student.overall_progress}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${getProgressColor(student.overall_progress)} transition-all`}
                                style={{ width: `${student.overall_progress}%` }}
                              />
                            </div>
                          </div>
                          
                          {/* Stats */}
                          <div className="hidden lg:flex items-center gap-3 text-xs">
                            <div className="text-center px-2">
                              <p className="font-semibold text-blue-600">{student.content_completed}/{student.content_total}</p>
                              <p className="text-gray-500">Content</p>
                            </div>
                            <div className="text-center px-2">
                              <p className="font-semibold text-green-600">{student.assignments_submitted}/{student.assignments_total}</p>
                              <p className="text-gray-500">Assignments</p>
                            </div>
                            <div className="text-center px-2">
                              <p className="font-semibold text-purple-600">{student.avg_quiz_score}%</p>
                              <p className="text-gray-500">Quiz Avg</p>
                            </div>
                          </div>
                          
                          {/* Status Badge */}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(student.status)}`}>
                            {getStatusLabel(student.status)}
                          </span>
                          
                          {expandedStudent === student.student_db_id ? (
                            <ChevronUp className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedStudent === student.student_db_id && studentDetails[student.student_db_id] && (
                      <div className="border-t bg-gray-50 p-4">
                        <div className="grid md:grid-cols-3 gap-4">
                          {/* Content Progress */}
                          <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <BookOpen className="h-4 w-4 text-blue-600" />
                              Content Progress
                            </h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Completed</span>
                                <span className="font-semibold">{studentDetails[student.student_db_id].content?.total_completed || 0}</span>
                              </div>
                              {studentDetails[student.student_db_id].content?.completed?.slice(0, 3).map((c, i) => (
                                <p key={i} className="text-xs text-gray-600 truncate">• {c.title}</p>
                              ))}
                            </div>
                          </div>
                          
                          {/* Assignment Progress */}
                          <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <ClipboardList className="h-4 w-4 text-green-600" />
                              Assignments
                            </h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Submitted</span>
                                <span className="font-semibold">{studentDetails[student.student_db_id].assignments?.total_submitted || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Average Marks</span>
                                <span className="font-semibold">{studentDetails[student.student_db_id].assignments?.average_marks || 0}</span>
                              </div>
                              {studentDetails[student.student_db_id].assignments?.submissions?.slice(0, 2).map((s, i) => (
                                <div key={i} className="text-xs flex justify-between">
                                  <span className="text-gray-600 truncate flex-1">{s.assignment_title}</span>
                                  <span className={s.status === 'graded' ? 'text-green-600' : 'text-yellow-600'}>
                                    {s.status === 'graded' ? `${s.marks_obtained}/${s.total_marks}` : 'Pending'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Quiz Progress */}
                          <div className="bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <FileQuestion className="h-4 w-4 text-purple-600" />
                              Quizzes
                            </h4>
                            <div className="space-y-2">
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Attempted</span>
                                <span className="font-semibold">{studentDetails[student.student_db_id].quizzes?.total_attempts || 0}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Average Score</span>
                                <span className="font-semibold">{studentDetails[student.student_db_id].quizzes?.average_score || 0}%</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Passed</span>
                                <span className="font-semibold text-green-600">{studentDetails[student.student_db_id].quizzes?.passed_count || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Recent Activities */}
                        {studentDetails[student.student_db_id].recent_activities?.length > 0 && (
                          <div className="mt-4 bg-white p-4 rounded-lg border">
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <Clock className="h-4 w-4 text-gray-600" />
                              Recent Activities
                            </h4>
                            <div className="space-y-2">
                              {studentDetails[student.student_db_id].recent_activities.slice(0, 5).map((activity, i) => (
                                <div key={i} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    {activity.type === 'content_view' && <Eye className="h-4 w-4 text-blue-500" />}
                                    {activity.type === 'assignment_submission' && <ClipboardList className="h-4 w-4 text-green-500" />}
                                    {activity.type === 'quiz_attempt' && <FileQuestion className="h-4 w-4 text-purple-500" />}
                                    <span className="text-gray-700">{activity.title}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    {activity.score !== undefined && (
                                      <span className={activity.passed ? 'text-green-600' : 'text-red-600'}>
                                        {activity.score}%
                                      </span>
                                    )}
                                    {activity.marks !== undefined && (
                                      <span className="text-green-600">{activity.marks} marks</span>
                                    )}
                                    <span>{activity.date?.split('T')[0]}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
